import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map, Observable, of } from 'rxjs'
import { Backend } from 'src/app/shared/enums/enums'
import { Pair } from 'src/app/shared/models/main/pair'
import { DdcLoggerService } from '../log/ddc-logger.service'
import { ApiResponse } from '../state/library-state.service'

@Injectable({
  providedIn: 'root',
})
export class PairService {
  /**
   * Fetch a single pair by its ID
   */
  get_pair(pair_id: number): Observable<Pair> {
    this.logger.info('PAIR | get_pair | pair_id: ' + pair_id)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }
    return this.http
      .get<Pair>(`${Backend.HOST_CONTAINER + Backend.PAIR + pair_id + '/'}`, httpOptions)
      .pipe(
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('PAIR | get_pair | catchError | errorMessage: ' + errorMessage)
          return of(null)
        }),
      )
  }
  constructor(
    private http: HttpClient,
    private logger: DdcLoggerService,
  ) {}

  get_pairs(): Observable<ApiResponse<Pair>> {
    this.logger.info('PAIR | get_pairs')
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http.get<ApiResponse<Pair>>(`${Backend.HOST_CONTAINER + Backend.PAIR}`, httpOptions)
  }

  /**
   * Fetches pairs using a full URL (for paginated API 'next' links)
   */
  getPairsByUrl(url: string): Observable<any> {
    this.logger.info('PAIR | getPairsByUrl | url: ' + url)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }
    return this.http.get(url, httpOptions).pipe(
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('PAIR | getPairsByUrl | catchError | errorMessage: ' + errorMessage)
        return of({ results: [], count: 0, detail: errorMessage })
      }),
    )
  }

  get_pairs_page(page: number, pageSize: number): Observable<ApiResponse<Pair>> {
    this.logger.info('PAIR | get_pairs_page | page: ' + page + ' | pageSize: ' + pageSize)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get<ApiResponse<Pair>>(
        `${Backend.HOST_CONTAINER + Backend.PAIR}?page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('PAIR | get_pairs_page | catchError | errorMessage: ' + errorMessage)
          return of({ results: [], count: 0, detail: errorMessage })
        }),
      )
  }

  get_user_pairs_page(username: string, page: number, pageSize: number): any {
    this.logger.info(
      'PAIR/username | get_user_pairs_page | username: ' +
        username +
        ' | page: ' +
        page +
        ' | pageSize: ' +
        pageSize,
    )

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.PAIR}?username=${username}&page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'PAIR/username | get_user_pairs_page | catchError | errorMessage: ' + errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }

  get_searched_pairs(target: string, maxRows: number = 10): any {
    this.logger.info(
      'PAIR/Search | get_searched_pairs | target: ' + target + ' | maxRows: ' + maxRows,
    )

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    // limit max rows to 10
    if (maxRows > 10 || maxRows < 1) maxRows = 10

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.PAIR}search/?max=${maxRows}&word=${target}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'PAIR/Search | get_searched_pairs | catchError | errorMessage: ' + errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }

  get_dic_pairs_page(dic_id: number, page: number, pageSize: number): any {
    this.logger.info(
      'DIC/PAIR | get_dic_pairs_page | dic_id: ' +
        dic_id +
        ' | page: ' +
        page +
        ' | pageSize: ' +
        pageSize,
    )

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.DIC}${dic_id}/pairs/?page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'DIC/PAIR | get_dic_pairs_page | catchError | errorMessage: ' + errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }

  add_pair(word_1_id: number, word_2_id: number, dic: any): any {
    this.logger.info('PAIR | add_pair | word_1_id: ' + word_1_id + ' | word_2_id: ' + word_2_id)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      word_1: word_1_id,
      word_2: word_2_id,
      dictionaries: dic,
    }

    return this.http.post(`${Backend.HOST_CONTAINER + Backend.PAIR}`, userData, httpOptions).pipe(
      map((res: any) => {
        return { success: true, res: res }
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('PAIR | add_pair | catchError | errorMessage: ' + errorMessage)
        return of({ success: false, res: errorMessage })
      }),
    )
  }

  edit_pair(pair_data: any): any {
    this.logger.info('PAIR | edit_pair | pair_data: ' + pair_data)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .patch(
        `${Backend.HOST_CONTAINER + Backend.PAIR + pair_data.id + '/'}`,
        pair_data,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('PAIR | edit_pair | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }

  delete_pair(pair_id: number): any {
    this.logger.info('PAIR | delete_pair | pair_id: ' + pair_id)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .delete(`${Backend.HOST_CONTAINER + Backend.PAIR + pair_id + '/'}`, httpOptions)
      .pipe(
        map((res: any) => {
          return { success: true, res: res }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('PAIR | delete_pair | catchError | errorMessage: ' + errorMessage)
          return of({ success: false, res: errorMessage })
        }),
      )
  }
}
